/**
 * Money formatting primitive — single source of truth (SSOT).
 *
 * The backend is authoritative for every monetary value: amounts arrive
 * already converted into the display currency and already markup/commission
 * adjusted. The frontend NEVER recomputes a price — it only formats.
 *
 * Two helpers:
 *  - `formatAmount` renders just the number ("1 234,56"). Use it where the
 *    currency code is rendered as a separate token in the template (the
 *    dominant existing pattern in Confirmation / SideTicket).
 *  - `formatMoney` renders amount + currency code ("1 234,56 EUR"). Use it
 *    for new call sites that want a single self-contained string.
 *
 * Currencies are stored in mixed case across the codebase ("Eur", "Mad").
 * We never feed the raw value to `Intl.NumberFormat({ style: 'currency' })`
 * (it would throw on "Eur") — we format the number and append the code,
 * upper-cased, exactly like the legacy `fmtAmount` + `{{ currency }}` pattern.
 */

/**
 * Format a numeric amount with 2 fraction digits, no currency.
 * Mirrors the legacy `fmtAmount` / `formatAmount` helpers.
 *
 * @param amount   number or numeric string; null/NaN renders as "0,00"/locale-equivalent
 * @param locale   BCP-47 locale (default: runtime/agent locale)
 */
export function formatAmount(amount: number | string | null | undefined, locale?: string): string {
  const n = typeof amount === 'string' ? Number.parseFloat(amount) : Number(amount ?? 0)
  const safe = Number.isFinite(n) ? n : 0
  return safe.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Symbol per currency code. Known currencies render with their symbol
 * ("1 234,56 €"); unknown ones fall back to the upper-cased code
 * ("1 234,56 MAD"). We never feed mixed-case codes like "Eur" to
 * `Intl.NumberFormat({ style: 'currency' })` (it would throw).
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
}

/**
 * Format amount + currency as one string ("1 234,56 €").
 * Renders the currency symbol when known, else the upper-cased code,
 * tolerating mixed-case codes like "Eur"/"Mad".
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  locale?: string,
): string {
  const num = formatAmount(amount, locale)
  const ccy = (currency ?? '').trim()
  if (!ccy) return num
  const code = ccy.toUpperCase()
  return `${num} ${CURRENCY_SYMBOLS[code] ?? code}`
}
